/**
 * Tour — lightweight in-context guided tour component.
 *
 * Why not react-joyride? React 19 compatibility is unreliable, and we already
 * have radix-popover + framer-motion in the stack. This is ~150 lines, fully
 * styled to match the rest of MASKIL UI, and gives us full control.
 *
 * Usage:
 *   <Tour
 *     tourId="tenders-overview"
 *     steps={[
 *       { target: '[data-tour="tenders-filters"]',
 *         title: 'Filtry przetargów',
 *         content: 'Tu zawężasz wyniki...',
 *         placement: 'bottom' },
 *       ...
 *     ]}
 *     active={showTour}
 *     onComplete={() => setShowTour(false)}
 *   />
 *
 * Each tour is identified by tourId — completion saved in localStorage so
 * users don't see the same tour twice. Override with `force=true`.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';

const STORAGE_PREFIX = 'maskil_tour_';
const HIGHLIGHT_PADDING = 6;


export function shouldShowTour(tourId) {
  if (typeof window === 'undefined') return false;
  return !localStorage.getItem(STORAGE_PREFIX + tourId);
}


export function markTourSeen(tourId) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_PREFIX + tourId, new Date().toISOString());
}


export function resetTour(tourId) {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_PREFIX + tourId);
}


export function resetAllTours() {
  if (typeof window === 'undefined') return;
  Object.keys(localStorage)
    .filter((k) => k.startsWith(STORAGE_PREFIX))
    .forEach((k) => localStorage.removeItem(k));
}


/**
 * Compute tooltip position relative to target element.
 * Returns { top, left, placement, arrow }.
 */
function computePosition(target, placement = 'bottom') {
  if (!target) return null;
  const r = target.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const TOOLTIP_W = 360;
  const TOOLTIP_H = 180;  // rough estimate
  const GAP = 12;

  // Auto-flip if not enough space
  let actual = placement;
  if (placement === 'bottom' && r.bottom + TOOLTIP_H + GAP > vh) actual = 'top';
  if (placement === 'top' && r.top - TOOLTIP_H - GAP < 0) actual = 'bottom';
  if (placement === 'right' && r.right + TOOLTIP_W + GAP > vw) actual = 'left';
  if (placement === 'left' && r.left - TOOLTIP_W - GAP < 0) actual = 'right';

  let top, left;
  switch (actual) {
    case 'top':
      top = r.top - TOOLTIP_H - GAP;
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      break;
    case 'right':
      top = r.top + r.height / 2 - TOOLTIP_H / 2;
      left = r.right + GAP;
      break;
    case 'left':
      top = r.top + r.height / 2 - TOOLTIP_H / 2;
      left = r.left - TOOLTIP_W - GAP;
      break;
    default:  // bottom
      top = r.bottom + GAP;
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
  }

  // Clamp to viewport
  left = Math.max(8, Math.min(left, vw - TOOLTIP_W - 8));
  top = Math.max(8, Math.min(top, vh - TOOLTIP_H - 8));

  return { top, left, placement: actual };
}


export default function Tour({ tourId, steps, active, onComplete, onSkip, lang = 'pl' }) {
  const [idx, setIdx] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tipPos, setTipPos] = useState(null);
  const rafRef = useRef(null);

  const step = active && steps?.[idx];

  // Locate target element and compute position
  const updatePosition = useCallback(() => {
    if (!step) return;
    const target = document.querySelector(step.target);
    if (!target) {
      setTargetRect(null);
      setTipPos(null);
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    const r = target.getBoundingClientRect();
    setTargetRect({
      top: r.top - HIGHLIGHT_PADDING,
      left: r.left - HIGHLIGHT_PADDING,
      width: r.width + HIGHLIGHT_PADDING * 2,
      height: r.height + HIGHLIGHT_PADDING * 2,
    });
    setTipPos(computePosition(target, step.placement || 'bottom'));
  }, [step]);

  useEffect(() => {
    if (!active) return;
    setIdx(0);
  }, [active, tourId]);

  useEffect(() => {
    if (!active || !step) return;
    // Wait one frame to let scrollIntoView finish, then poll for stable position
    let attempts = 0;
    const tick = () => {
      updatePosition();
      attempts += 1;
      if (attempts < 8) {
        rafRef.current = setTimeout(tick, 60);
      }
    };
    tick();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      if (rafRef.current) clearTimeout(rafRef.current);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [active, idx, step, updatePosition]);

  if (!active || !steps?.length) return null;
  const isLast = idx === steps.length - 1;
  const total = steps.length;

  const handleNext = () => {
    if (isLast) {
      markTourSeen(tourId);
      if (onComplete) onComplete();
    } else {
      setIdx(idx + 1);
    }
  };

  const handlePrev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const handleSkip = () => {
    markTourSeen(tourId);
    if (onSkip) onSkip();
    else if (onComplete) onComplete();
  };

  return (
    <>
      {/* Dimmed backdrop with cutout around target */}
      <div
        className="fixed inset-0 z-[60] pointer-events-none"
        style={{
          background: targetRect
            ? `radial-gradient(ellipse ${targetRect.width / 1.4}px ${targetRect.height / 1.4}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, transparent 60%, rgba(0,0,0,0.6) 100%)`
            : 'rgba(0,0,0,0.5)',
          transition: 'background 200ms',
        }}
        data-testid="tour-backdrop"
      />

      {/* Highlight ring */}
      {targetRect && (
        <div
          className="fixed z-[61] pointer-events-none rounded-lg border-2 border-primary shadow-[0_0_0_4px_rgba(99,102,241,0.25)]"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            transition: 'all 220ms ease-out',
          }}
        />
      )}

      {/* Tooltip card */}
      {tipPos && step && (
        <div
          className="fixed z-[62] w-[360px] rounded-xl border border-border bg-card shadow-2xl"
          style={{
            top: tipPos.top,
            left: tipPos.left,
            transition: 'top 220ms, left 220ms',
          }}
          data-testid={`tour-step-${idx}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-border">
            <div>
              <div className="text-xs text-muted-foreground font-medium">
                {lang === 'pl' ? 'Krok' : 'Step'} {idx + 1} / {total}
              </div>
              <div className="text-sm font-semibold mt-0.5">{step.title}</div>
            </div>
            <button
              type="button"
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground p-1 -mr-1 -mt-1"
              data-testid="tour-skip"
              aria-label={lang === 'pl' ? 'Pomiń' : 'Skip'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3 text-sm text-muted-foreground leading-relaxed">
            {typeof step.content === 'string' ? <p>{step.content}</p> : step.content}
          </div>

          {/* Progress dots + actions */}
          <div className="px-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`block rounded-full transition-all ${
                    i === idx ? 'w-4 h-1.5 bg-primary' : i < idx ? 'w-1.5 h-1.5 bg-primary/40' : 'w-1.5 h-1.5 bg-border'
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {idx > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrev}
                  className="h-7 px-2"
                  data-testid="tour-prev"
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-0.5" />
                  {lang === 'pl' ? 'Wstecz' : 'Back'}
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="h-7 px-3 gap-1"
                data-testid={isLast ? 'tour-finish' : 'tour-next'}
              >
                {isLast ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    {lang === 'pl' ? 'Zakończ' : 'Finish'}
                  </>
                ) : (
                  <>
                    {lang === 'pl' ? 'Dalej' : 'Next'}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
